import React from "react";
import { useSelector } from "react-redux";
import { Box, ButtonBase } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckRounded from "@mui/icons-material/CheckRounded";
import LockOutlined from "@mui/icons-material/LockOutlined";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import BrandLogo from "../branding/BrandLogo";
import { selectActiveBrand } from "../../redux/reducers/brandReducer";
import colors from "../../theme/colors";
import { APP_TOKENS } from "./publicFormTokens";

/**
 * The persistent left rail: brand, progress, and the step list.
 *
 * Built from scratch rather than MUI's <Stepper> — this needs a per-step
 * sublabel, a "furthest reached" notion (steps behind you are revisitable, steps
 * ahead are not), and a trust footer, none of which Stepper models.
 *
 * Painted in the brand's HEADER colors (`headerBg` / `headerFg`), the same pair
 * the authenticated TopBar uses, so a borrower arriving from a broker's link
 * sees the same chrome the broker does. Everything on the rail is derived from
 * `headerFg` at varying alpha rather than from the palette — a brand can set any
 * header background, and a fixed accent would be unreadable against some of
 * them.
 */
const PublicFormProgressRail = ({
  formTitle,
  steps,
  activeStep,
  furthestStep,
  onStepSelect,
  hideLogo,
  customLogoName,
}) => {
  const { headerBg, headerFg } = useSelector(selectActiveBrand);
  const pct = Math.round((activeStep / steps.length) * 100);

  const muted = alpha(headerFg, 0.65);
  const hairline = alpha(headerFg, 0.14);

  return (
    <Flx
      column
      sx={{
        width: APP_TOKENS.railWidth,
        flexShrink: 0,
        background: headerBg,
        borderRight: `1px solid ${hairline}`,
        position: "sticky",
        top: 0,
        height: "100vh",
        p: 3,
        display: { xs: "none", md: "flex" },
      }}
    >
      <Flx column gap={3} sx={{ flex: 1, minHeight: 0 }}>
        <Flx column gap={2.5}>
          {hideLogo ? null : (
            <BrandLogo height={34} maxWidth={200} color={headerFg} />
          )}
          {customLogoName && (
            <Txt component="div" sx={{ fontWeight: 700, color: headerFg }}>
              {customLogoName}
            </Txt>
          )}

          <Flx column gap={1}>
            <Flx between centerY>
              <Txt
                component="div"
                sx={{ fontSize: 12, fontWeight: 600, color: headerFg }}
              >
                {formTitle}
              </Txt>
              <Txt component="div" sx={{ fontSize: 12, color: muted }}>
                {`${pct}%`}
              </Txt>
            </Flx>
            <ProgressBar pct={pct} fg={headerFg} />
          </Flx>
        </Flx>

        {/* No gap: each row owns its own bottom spacing so the connector line
            can run through it. A gap here would break the track. */}
        <Flx column gap={0} sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          {steps.map((step, index) => (
            <RailStep
              key={step.label}
              index={index}
              step={step}
              isLast={index === steps.length - 1}
              state={
                index < furthestStep
                  ? "complete"
                  : index === activeStep
                    ? "current"
                    : "upcoming"
              }
              // Only completed ground is navigable — jumping ahead would skip
              // the per-step validation gate in the footer.
              disabled={index > furthestStep}
              onClick={() => onStepSelect(index)}
              bg={headerBg}
              fg={headerFg}
            />
          ))}
        </Flx>

        <Flx centerY gap={1} sx={{ pt: 2, borderTop: `1px solid ${hairline}` }}>
          <LockOutlined sx={{ fontSize: 15, color: muted }} />
          <Txt component="div" sx={{ fontSize: 11.5, color: muted }}>
            Your information is transmitted securely and saved to this browser as
            you go.
          </Txt>
        </Flx>
      </Flx>
    </Flx>
  );
};

const ProgressBar = ({ pct, fg }) => (
  <Box
    sx={{
      height: 5,
      borderRadius: 99,
      background: alpha(fg, 0.18),
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        height: "100%",
        width: `${pct}%`,
        borderRadius: 99,
        background: fg,
        transition: "width 260ms ease",
      }}
    />
  </Box>
);

/**
 * Geometry for the connector line, derived from the row's own padding so the two
 * can't drift apart:
 *   ITEM_PAD (10) + half the 22px marker = 21px to the marker's centre.
 * The line is absolutely positioned rather than stacked under the marker — as a
 * flex child it could only be a fixed-height stub, which left a gap before the
 * next marker and floated whenever a label wrapped to two lines.
 */
const ITEM_PAD_PX = 10;
const MARKER_PX = 22;
const LINE_W_PX = 2;
const LINE_LEFT_PX = ITEM_PAD_PX + MARKER_PX / 2 - LINE_W_PX / 2;
const LINE_TOP_PX = ITEM_PAD_PX + MARKER_PX + 2;

const RailStep = ({
  index,
  step,
  state,
  disabled,
  isLast,
  onClick,
  bg,
  fg,
}) => {
  const isCurrent = state === "current";
  const isComplete = state === "complete";

  return (
    <Box sx={{ position: "relative" }}>
      {!isLast && (
        // Runs past this row's bottom edge into the next row's top padding, so
        // the steps read as one connected track.
        //
        // zIndex 1 keeps it above the row's hover/current pill: both are
        // positioned with z-index auto, so without it the pill — a later sibling
        // — paints over the line and breaks the track wherever a row is hovered
        // or active. It clears the marker vertically, so nothing else overlaps.
        <Box
          sx={{
            position: "absolute",
            zIndex: 1,
            left: `${LINE_LEFT_PX}px`,
            top: `${LINE_TOP_PX}px`,
            bottom: "-6px",
            width: `${LINE_W_PX}px`,
            borderRadius: 99,
            // Brand blue on completed segments, matching the markers it joins —
            // tpo used `fg` here, which now leaves an ink line running between
            // blue markers and breaks the track visually.
            background: isComplete ? colors.action : alpha(fg, 0.2),
          }}
        />
      )}
      <ButtonBase
        disabled={disabled}
        onClick={onClick}
        sx={{
          textAlign: "left",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          gap: 1.5,
          p: `${ITEM_PAD_PX}px`,
          borderRadius: "8px",
          width: "100%",
          // LENDMARQ DIVERGENCE: the current step reads as a brand-blue chip —
          // tinted fill plus a blue hairline — matching the active-tab chip the
          // dashboard uses. tpo tints with `headerFg`, which on the white rail
          // (see brandReducer) is a grey pill that doesn't read as selected.
          // Hover stays neutral so it can't be mistaken for the active step.
          background: isCurrent ? alpha(colors.action, 0.1) : "transparent",
          border: `1px solid ${isCurrent ? alpha(colors.action, 0.35) : "transparent"}`,
          "&:hover": {
            background: disabled
              ? "transparent"
              : isCurrent
                ? alpha(colors.action, 0.14)
                : alpha(fg, 0.06),
          },
          "&.Mui-disabled": { opacity: 1 },
        }}
      >
        <Marker
          index={index}
          isCurrent={isCurrent}
          isComplete={isComplete}
          bg={bg}
          fg={fg}
        />
        <Flx column gap={0.25}>
          <Txt
            component="div"
            sx={{
              fontSize: 13.5,
              fontWeight: isCurrent ? 700 : 500,
              // The active step's label takes the brand blue with its chip;
              // completed steps stay ink so "where I am" reads at a glance.
              color: isCurrent
                ? colors.action
                : isComplete
                  ? fg
                  : alpha(fg, 0.65),
            }}
          >
            {step.label}
          </Txt>
          <Txt
            component="div"
            sx={{
              fontSize: 11.5,
              lineHeight: 1.35,
              color: alpha(fg, 0.55),
            }}
          >
            {step.sublabel}
          </Txt>
        </Flx>
      </ButtonBase>
    </Box>
  );
};

/**
 * Reached markers fill with the brand blue and knock the numeral out in white.
 *
 * LENDMARQ DIVERGENCE: tpo fills them with `headerFg` instead, and its comment
 * warns that the brand accent would put white on a mid-tone at ~3:1, which an
 * 11px numeral can't carry. That held for Marq's #56A0D3; Lendmarq's #1F5AF2
 * carries white at ~4.9:1, so the marker can be the brand color. `bg` is no
 * longer the numeral color — on the white rail it would be invisible.
 */
const Marker = ({ index, isCurrent, isComplete, fg }) => {
  const filled = isComplete || isCurrent;

  return (
    <Flx
      centerX
      centerY
      sx={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        flexShrink: 0,
        fontSize: 11,
        fontWeight: 700,
        color: filled ? colors.white : alpha(fg, 0.65),
        background: filled ? colors.action : "transparent",
        border: filled ? "none" : `1.5px solid ${alpha(fg, 0.35)}`,
      }}
    >
      {isComplete ? <CheckRounded sx={{ fontSize: 14 }} /> : index + 1}
    </Flx>
  );
};

export default PublicFormProgressRail;
